<script lang="ts">
  import { onDestroy } from 'svelte';
  import { liveCommunityMembersStore } from '../../lib/convexRealtime';
  import {
    decideCommunityApplication,
    setCommunityMemberRole,
    removeCommunityMember,
    setMembershipWelcomeSent
  } from '../../lib/convexApi';
  import type { CommunityMemberEntry } from '../../lib/types';
  import {
    buildWhatsappUrl,
    firstNameOf,
    normalizeWhatsappNumber,
    renderWelcomeMessage
  } from '../../lib/whatsapp';
  import Notice from '../ui/Notice.svelte';
  import LoadingBlock from '../ui/LoadingBlock.svelte';
  import ResponseAnswers from './ResponseAnswers.svelte';

  export let communityId: string;
  export let isOwner = false;
  /** Community name/slug/template for the post-accept "say hi" message. */
  export let communityName = '';
  export let communitySlug = '';
  export let welcomeMessage: string | null = null;

  let store: ReturnType<typeof liveCommunityMembersStore> | null = null;
  let unsubscribe: (() => void) | null = null;
  let subscribedId: string | null = null;

  let members: CommunityMemberEntry[] = [];
  let loading = true;
  let error: string | null = null;

  let actionError: string | null = null;
  let pendingActionId: string | null = null;

  $: if (communityId && communityId !== subscribedId) {
    subscribedId = communityId;
    unsubscribe?.();
    store?.dispose();
    store = liveCommunityMembersStore(communityId);
    unsubscribe = store.subscribe(state => {
      members = state.data ?? [];
      loading = state.loading;
      error = state.error;
    });
  }

  onDestroy(() => {
    unsubscribe?.();
    store?.dispose();
  });

  $: applications = members.filter(entry => entry.status === 'pending');
  // Accepted here (decidedAt set — legacy rows never had it) and not yet
  // greeted: the manager's next task after clicking Approve.
  $: toWelcome = members
    .filter(entry => entry.status === 'active' && entry.decidedAt && !entry.welcomeSentAt)
    .sort((a, b) => (b.decidedAt ?? 0) - (a.decidedAt ?? 0));
  $: welcomeUrl =
    typeof window !== 'undefined' && communitySlug
      ? `${window.location.origin}/c/${communitySlug}`
      : 'https://gravity-events.app';
  $: activeMembers = members
    .filter(entry => entry.status === 'active')
    .sort((a, b) => {
      const rank = { owner: 0, admin: 1, member: 2 };
      const byRole = (rank[a.role] ?? 3) - (rank[b.role] ?? 3);
      if (byRole !== 0) return byRole;
      return (a.person?.name ?? '').localeCompare(b.person?.name ?? '');
    });

  async function runAction(id: string, action: () => Promise<unknown>) {
    pendingActionId = id;
    actionError = null;
    try {
      await action();
      await store?.refresh();
    } catch (err) {
      actionError = err instanceof Error ? err.message : 'Action failed. Please try again.';
    } finally {
      pendingActionId = null;
    }
  }

  const decide = (entry: CommunityMemberEntry, approve: boolean) =>
    runAction(entry.membershipId, () => decideCommunityApplication(entry.membershipId, approve));

  const markWelcome = (entry: CommunityMemberEntry, sent: boolean) =>
    runAction(entry.membershipId, () => setMembershipWelcomeSent(entry.membershipId, sent));

  function welcomeFor(entry: CommunityMemberEntry) {
    const name = entry.person?.name ?? '';
    const text = renderWelcomeMessage(welcomeMessage, {
      first: firstNameOf(name),
      name,
      community: communityName,
      url: welcomeUrl
    });
    const phone = normalizeWhatsappNumber(entry.phone);
    return {
      text,
      phone,
      href: phone ? buildWhatsappUrl(phone.digits, text) : null
    };
  }

  let copiedId: string | null = null;
  async function copyWelcome(entry: CommunityMemberEntry) {
    try {
      await navigator.clipboard.writeText(welcomeFor(entry).text);
      copiedId = entry.membershipId;
      setTimeout(() => (copiedId = null), 1600);
    } catch (err) {
      console.error('Copy failed', err);
    }
  }

  const setRole = (entry: CommunityMemberEntry, role: 'admin' | 'member') =>
    runAction(entry.membershipId, () => setCommunityMemberRole(entry.membershipId, role));

  const remove = (entry: CommunityMemberEntry) => {
    const name = entry.person?.name ?? 'this member';
    if (!window.confirm(`Remove ${name} from the community?`)) return;
    void runAction(entry.membershipId, () => removeCommunityMember(entry.membershipId));
  };
</script>

<section class="members surface surface--sunken" aria-labelledby="members-heading">
  <h3 id="members-heading">Members</h3>

  {#if actionError}
    <Notice variant="error">{actionError}</Notice>
  {/if}

  {#if loading && members.length === 0}
    <LoadingBlock label="Loading members…" />
  {:else if error}
    <Notice variant="error">Could not load members: {error}</Notice>
  {:else}
    {#if applications.length > 0}
      <div class="members__group">
        <h4>Applications ({applications.length})</h4>
        <ul>
          {#each applications as entry (entry.membershipId)}
            <li class="member-row member-row--pending">
              <div class="member-row__info">
                <span class="member-row__name">{entry.person?.name ?? 'Unknown'}</span>
                {#if entry.person?.email}
                  <span class="member-row__meta">{entry.person.email}</span>
                {/if}
                {#if entry.invitedBy}
                  <span class="member-row__meta member-row__provenance">
                    Invited by {entry.invitedBy.personName}{entry.invitedBy.eventName
                      ? ` · ${entry.invitedBy.eventName}`
                      : ''}
                  </span>
                {/if}
                {#if entry.applicationNote}
                  <blockquote class="member-row__note">“{entry.applicationNote}”</blockquote>
                {/if}
                {#if entry.applicationResponse}
                  <details class="member-row__answers">
                    <summary>Application answers</summary>
                    <ResponseAnswers response={entry.applicationResponse} />
                  </details>
                {/if}
              </div>
              <div class="member-row__actions">
                <button
                  type="button"
                  class="accent"
                  disabled={pendingActionId === entry.membershipId}
                  on:click={() => decide(entry, true)}
                >
                  Approve
                </button>
                <button
                  type="button"
                  class="ghost"
                  disabled={pendingActionId === entry.membershipId}
                  on:click={() => decide(entry, false)}
                >
                  Reject
                </button>
              </div>
            </li>
          {/each}
        </ul>
      </div>
    {/if}

    {#if toWelcome.length > 0}
      <div class="members__group">
        <h4>Say hi ({toWelcome.length})</h4>
        <ul>
          {#each toWelcome as entry (entry.membershipId)}
            {@const welcome = welcomeFor(entry)}
            <li class="member-row member-row--welcome">
              <div class="member-row__info">
                <span class="member-row__name">{entry.person?.name ?? 'Unknown'}</span>
                <span class="member-row__meta">
                  Accepted — tell them.
                  {#if welcome.phone}
                    WhatsApp +{welcome.phone.digits}{welcome.phone.guessed ? ' (country code assumed)' : ''}
                  {:else if entry.phone}
                    Phone “{entry.phone}” didn't parse — copy the text and send it by hand.
                  {:else}
                    No phone on the application{entry.person?.email ? ` — email ${entry.person.email}` : ''}.
                  {/if}
                </span>
                <details class="member-row__answers">
                  <summary>Message preview</summary>
                  <p class="member-row__preview">{welcome.text}</p>
                </details>
              </div>
              <div class="member-row__actions">
                {#if welcome.href}
                  <a class="button accent member-row__wa" href={welcome.href} target="_blank" rel="noopener noreferrer">
                    WhatsApp
                  </a>
                {/if}
                <button type="button" class="secondary" on:click={() => copyWelcome(entry)}>
                  {copiedId === entry.membershipId ? 'Copied' : 'Copy text'}
                </button>
                <button
                  type="button"
                  class="ghost"
                  disabled={pendingActionId === entry.membershipId}
                  on:click={() => markWelcome(entry, true)}
                >
                  Message sent
                </button>
              </div>
            </li>
          {/each}
        </ul>
      </div>
    {/if}

    <div class="members__group">
      <h4>Active ({activeMembers.length})</h4>
      <ul>
        {#each activeMembers as entry (entry.membershipId)}
          <li class="member-row">
            <div class="member-row__info">
              <span class="member-row__name">
                {entry.person?.name ?? 'Unknown'}
                {#if entry.role !== 'member'}
                  <span class="member-row__role member-row__role--{entry.role}">{entry.role}</span>
                {/if}
              </span>
              {#if entry.person?.email}
                <span class="member-row__meta">{entry.person.email}</span>
              {/if}
            </div>
            <div class="member-row__actions">
              {#if entry.role === 'member'}
                {#if isOwner}
                  <button
                    type="button"
                    class="secondary"
                    disabled={pendingActionId === entry.membershipId}
                    on:click={() => setRole(entry, 'admin')}
                  >
                    Make admin
                  </button>
                {/if}
                <button
                  type="button"
                  class="ghost"
                  disabled={pendingActionId === entry.membershipId}
                  on:click={() => remove(entry)}
                >
                  Remove
                </button>
              {:else if entry.role === 'admin' && isOwner}
                <button
                  type="button"
                  class="secondary"
                  disabled={pendingActionId === entry.membershipId}
                  on:click={() => setRole(entry, 'member')}
                >
                  Demote
                </button>
                <button
                  type="button"
                  class="ghost"
                  disabled={pendingActionId === entry.membershipId}
                  on:click={() => remove(entry)}
                >
                  Remove
                </button>
              {/if}
            </div>
          </li>
        {/each}
      </ul>
    </div>
  {/if}
</section>

<style>
  .members {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    padding: var(--space-5);
    border-radius: var(--radius-lg);
  }

  .members h3 {
    margin: 0;
    font-size: var(--font-size-md);
  }

  .members__group h4 {
    margin: 0 0 var(--space-3);
    font-size: var(--font-size-sm);
    color: var(--color-text-muted);
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .members__group ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .member-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-4);
    padding: var(--space-3) var(--space-4);
    border-radius: var(--radius-md);
    border: var(--border-subtle);
    background: var(--color-surface-850);
    flex-wrap: wrap;
  }

  .member-row--pending {
    border-color: rgb(var(--accent-rgb) / 0.35);
  }

  .member-row--welcome {
    border-style: dashed;
    border-color: rgb(var(--accent-rgb) / 0.5);
  }

  .member-row__preview {
    margin: 0;
    font-size: var(--font-size-xs);
    color: var(--color-text-secondary);
    white-space: pre-wrap;
    max-width: 60ch;
  }

  /* Anchor styled as the accent button (global button chrome only targets <button>). */
  .member-row__wa {
    display: inline-flex;
    align-items: center;
    text-decoration: none;
    font-family: var(--font-sans);
    font-weight: 700;
    letter-spacing: 0.02em;
    border: 1px solid transparent;
    background: var(--color-accent);
    color: var(--color-text-inverse);
    transition: background var(--transition-fast);
  }

  .member-row__wa:hover {
    background: var(--color-accent-bright);
  }

  .member-row__info {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    min-width: 0;
  }

  .member-row__name {
    font-weight: 600;
    font-size: var(--font-size-sm);
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
  }

  .member-row__role {
    font-size: var(--font-size-2xs);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    padding: 1px var(--space-2);
    border-radius: var(--radius-pill);
    border: var(--border-subtle);
  }

  .member-row__role--owner {
    color: var(--color-text-amber);
    border-color: rgb(var(--accent-rgb) / 0.4);
  }

  .member-row__role--admin {
    color: var(--color-accent-neon-400);
    border-color: rgba(78, 224, 195, 0.35);
  }

  .member-row__meta {
    font-size: var(--font-size-xs);
    color: var(--color-text-muted);
  }

  .member-row__note {
    margin: var(--space-1) 0 0;
    padding-left: var(--space-3);
    border-left: 2px solid rgb(var(--accent-rgb) / 0.35);
    font-size: var(--font-size-xs);
    color: var(--color-text-secondary);
    font-style: italic;
  }

  .member-row__provenance {
    color: var(--color-text-amber);
  }

  .member-row__answers {
    margin-top: var(--space-2);
  }

  .member-row__answers summary {
    font-size: var(--font-size-xs);
    color: var(--color-text-muted);
    cursor: pointer;
  }

  .member-row__answers[open] summary {
    margin-bottom: var(--space-2);
  }

  .member-row__actions {
    display: flex;
    gap: var(--space-2);
    flex-shrink: 0;
  }

  .member-row__actions button,
  .member-row__actions .button {
    font-size: var(--font-size-xs);
    padding: var(--space-2) var(--space-3);
  }
</style>
